<template>
    <div class="dreamina-model-page">
        <!-- Video Generator Section -->
        <ModelHeroSection ref="heroRef" :title="t('dreamina-3.generatorTitle')" :description="generatorDescription"
            model-name="Dreamina 3.0" model-key="DREAMINA_3" :tabs="tabs" redirect-url="/ai-video"
            @generate="handleGenerate">
            <template #logo>
                <div class="dreamina-logo">D</div>
            </template>
        </ModelHeroSection>

        <!-- Core Features Section -->
        <CoreFeaturesSection :title="t('dreamina-3.coreFeaturesTitle')" :features="coreFeatures" />

        <!-- How to Use Section -->
        <HowToUseSection :title="t('dreamina-3.howToUseTitle')" :steps="howToUseSteps" />

        <!-- YouTube Videos Section -->
        <YouTubeCarouselSection v-if="youtubeVideos.length > 0" :title="t('dreamina-3.youtubeTitle')"
            :videos="youtubeVideos" />

        <!-- X Posts Section -->
        <TwitterPosts v-if="twitterTweetUrls.length > 0" :title="t('dreamina-3.xPostsTitle')"
            :tweet-urls="twitterTweetUrls" />

        <!-- Explore Other Models Section -->
        <ExploreModelsSection :title="t('dreamina-3.exploreModelsTitle')" :models="exploreModels" />

        <!-- FAQ Section -->
        <FAQSection :title="t('dreamina-3.faqTitle')" :faqs="faqData" />

        <!-- CTA Section -->
        <CTASection :title="t('dreamina-3.ctaTitle')" :description="t('dreamina-3.ctaDescription')"
            :button-text="t('dreamina-3.ctaButton')" :disclaimer="t('dreamina-3.ctaDisclaimer')"
            @click="scrollToGenerator" />
    </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { useHead, useRequestURL } from '#imports'
import { useMultilingualSeo } from '~/composables/useMultilingualSeo'
import { useHowToSeo } from '~/composables/useHowToSeo'
import { useVideoSeo } from '~/composables/useVideoSeo'
import { useI18n } from '~/composables/useI18n'
import {
    ModelHeroSection,
    CoreFeaturesSection,
    HowToUseSection,
    YouTubeCarouselSection,
    ExploreModelsSection,
    FAQSection,
    CTASection,
} from '~/components/model/video'

const { t } = useI18n()
const requestUrl = useRequestURL()
const requestUrlHref = requestUrl.href

const demoVideos = {
    feature1: 'https://static.weryai.com/cms/dreamina3.0_video_01.mp4',
    feature2: 'https://static.weryai.com/cms/dreamina3.0_video_02.mp4',
    feature3: 'https://static.weryai.com/cms/dreamina3.0_video_03.mp4',
    feature4: 'https://static.weryai.com/cms/dreamina3.0_video_04.mp4',
} as const

definePageMeta({
    layout: 'website',
})

useMultilingualSeo({ pageType: 'video_dreamina_3' })

// BreadcrumbList SEO
useHead({
    script: [
        {
            type: 'application/ld+json',
            innerHTML: computed(() =>
                JSON.stringify({
                    '@context': 'https://schema.org',
                    '@type': 'BreadcrumbList',
                    itemListElement: [
                        {
                            '@type': 'ListItem',
                            position: 1,
                            name: 'Home',
                            item: 'https://www.weryai.com.cn/',
                        },
                        {
                            '@type': 'ListItem',
                            position: 2,
                            name: t('dreamina-3.generatorTitle'),
                            item: requestUrlHref,
                        },
                    ],
                })
            ),
        },
    ],
})

// Hero Section Ref
const heroRef = ref<InstanceType<typeof ModelHeroSection> | null>(null)

// Scroll to generator
const scrollToGenerator = () => {
    heroRef.value?.scrollToSection()
}

// Generator Description
const generatorDescription = computed(() => {
    return t('dreamina-3.generatorDescription')
})

// Tabs configuration
const tabs = computed(() => [
    { name: t('dreamina-3.tabImageToVideo'), external: false },
    { name: t('dreamina-3.tabTextToVideo'), external: false },
])

// Methods
const handleGenerate = (data: {
    type: 'image' | 'text'
    image?: File
    prompt?: string
}) => {
    // 组件内部已经处理了跳转逻辑
}

// Core Features Data
const coreFeatures = computed(() => [
    {
        title: t('dreamina-3.feature1Title'),
        description: t('dreamina-3.feature1Description'),
        videoUrl: demoVideos.feature1,
    },
    {
        title: t('dreamina-3.feature2Title'),
        description: t('dreamina-3.feature2Description'),
        videoUrl: demoVideos.feature2,
    },
    {
        title: t('dreamina-3.feature3Title'),
        description: t('dreamina-3.feature3Description'),
        videoUrl: demoVideos.feature3,
    },
    {
        title: t('dreamina-3.feature4Title'),
        description: t('dreamina-3.feature4Description'),
        videoUrl: demoVideos.feature4,
    },
])

// How to Use Steps
const howToUseSteps = computed(() => [
    {
        title: t('dreamina-3.step1Title'),
        description: t('dreamina-3.step1Description'),
    },
    {
        title: t('dreamina-3.step2Title'),
        description: t('dreamina-3.step2Description'),
    },
    {
        title: t('dreamina-3.step3Title'),
        description: t('dreamina-3.step3Description'),
    },
])

// HowTo SEO - 注入结构化数据
useHowToSeo(
    computed(() => t('dreamina-3.howToUseTitle')),
    howToUseSteps
)

// YouTube Videos Data
const youtubeVideos = ref([
    {
        title: 'Dreamina 3.0 Video 1',
        embedUrl: 'https://www.youtube.com/embed/ivO7VbyO1Gg',
    },
    {
        title: 'Dreamina 3.0 Video 2',
        embedUrl: 'https://www.youtube.com/embed/UnuU_X9-LpY',
    },
    {
        title: 'Dreamina 3.0 Video 3',
        embedUrl: 'https://www.youtube.com/embed/jsQ-nJ5TWlQ',
    },
    {
        title: 'Dreamina 3.0 Video 4',
        embedUrl: 'https://www.youtube.com/embed/P9NsWbiE4IU',
    },
    {
        title: 'Dreamina 3.0 Video 5',
        embedUrl: 'https://www.youtube.com/embed/cX6rHDv21s8',
    },
    {
        title: 'Dreamina 3.0 Video 6',
        embedUrl: 'https://www.youtube.com/embed/m4NDFPIh2KI',
    },
    {
        title: 'Dreamina 3.0 Video 7',
        embedUrl: 'https://www.youtube.com/embed/BIg-neyYdRc',
    },
    {
        title: 'Dreamina 3.0 Video 8',
        embedUrl: 'https://www.youtube.com/embed/6CJU_6po-18',
    },
])

// Video SEO - 注入 VideoObject 结构化数据
useVideoSeo(youtubeVideos.value)

// Twitter Tweet URLs Data
const twitterTweetUrls = ref<string[]>([
    'https://x.com/dreamina_ai/status/1943241135704674675',
    'https://x.com/underwoodxie96/status/2000116228862718126',
    'https://x.com/lepadphone/status/1907476686255632584',
    'https://x.com/HeyAmit_/status/1991107214799777979',
    'https://x.com/HeyAmit_/status/1993583033115676672',
    'https://x.com/dreamina_ai/status/1991446559284658463',
    'https://x.com/HeyAmit_/status/1990382000751173922',
    'https://x.com/dreamina_ai/status/1940427449835430067',
    'https://x.com/erhanmeydan/status/1940446282935321080',
    'https://x.com/aiwarts/status/1916373590892810656',
    'https://x.com/mhdfaran/status/1936022486069788696',
    'https://x.com/heydin_ai/status/1940469555106922858',
    'https://x.com/aiwarts/status/1916373504188158070',
    'https://x.com/00Bohr/status/1908652635529392471',
])

// Explore Other Models Data
const exploreModels = computed(() => [
    {
        title: 'Veo 3.1',
        icon: 'https://static.weryai.com/growth/image/3ed8c9e1a1ec3b37133c608c292821cb.png',
        path: '/video/veo/veo-3-1',
    },
    {
        title: 'Sora 2',
        icon: 'https://static.weryai.com/growth/image/7ac1a1457b1e788829a2da1d178879bb.png',
        path: '/video/sora/sora-2',
    },
    {
        title: 'Runway Gen-4.5',
        icon: 'https://static.weryai.com/growth/image/a65ca4242d4a0dc3f1b65e1d96471144.png',
        path: '/video/runway-ai-4-5',
    },
    {
        title: 'Kling 2.6',
        icon: 'https://static.weryai.com/growth/image/1fda745d93db271f6da3cedc576b515a.png',
        path: '/video/kling-2.6-pro',
    },
])

// FAQ Data
const faqData = computed(() => [
    {
        question: t('dreamina-3.faq1Question'),
        answer: t('dreamina-3.faq1Answer'),
    },
    {
        question: t('dreamina-3.faq2Question'),
        answer: t('dreamina-3.faq2Answer'),
    },
    {
        question: t('dreamina-3.faq3Question'),
        answer: t('dreamina-3.faq3Answer'),
    },
    {
        question: t('dreamina-3.faq4Question'),
        answer: t('dreamina-3.faq4Answer'),
    },
    {
        question: t('dreamina-3.faq5Question'),
        answer: t('dreamina-3.faq5Answer'),
    },
    {
        question: t('dreamina-3.faq6Question'),
        answer: t('dreamina-3.faq6Answer'),
    },
])

// SEO Meta Data - Handled by useMultilingualSeo

// Structured Data for SEO
const structuredData = computed(() => ({
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: t('dreamina-3.generatorTitle'),
    description: t('dreamina-3.generatorDescription'),
    applicationCategory: 'MultimediaApplication',
    operatingSystem: 'Web',
    offers: {
        '@type': 'Offer',
        price: '0',
        priceCurrency: 'USD',
    },
    provider: {
        '@type': 'Organization',
        name: '微鲤AI',
        url: 'https://www.weryai.com.cn',
    },
    featureList: [
        t('dreamina-3.feature1Title'),
        t('dreamina-3.feature2Title'),
        t('dreamina-3.feature3Title'),
        t('dreamina-3.feature4Title'),
    ],
}))

useHead({
    script: [
        {
            type: 'application/ld+json',
            innerHTML: computed(() => JSON.stringify(structuredData.value)),
        },
    ],
})
</script>

<style lang="less" scoped>
@import '~/assets/css/howToUse.css';

.dreamina-logo {
    width: 48px;
    height: 48px;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 24px;
    font-weight: bold;
    color: #fff;
}
</style>
